/**
 * Canonical driver-domain enums, shared between backend-tms and apps/web-tms.
 * Values are UPPERCASE to match the existing `driver_status_enum` Postgres
 * type and the string values stored inside `drivers.qualifications`/
 * `drivers.documents` JSONB columns. Changing case requires a migration +
 * data backfill; there is no user-visible benefit, so values stay as they
 * ship in production.
 *
 * USA-first ordering (FMCSA CDL classes before EU/TR categories) per the
 * project's market priority.
 */

export enum DriverStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ON_LEAVE = 'ON_LEAVE',
  SUSPENDED = 'SUSPENDED',
}

export const DRIVER_STATUS_LABELS: Record<DriverStatus, string> = {
  [DriverStatus.ACTIVE]: 'Active',
  [DriverStatus.INACTIVE]: 'Inactive',
  [DriverStatus.ON_LEAVE]: 'On Leave',
  [DriverStatus.SUSPENDED]: 'Suspended',
};

export enum DriverLicenseType {
  // US CDL classes (FMCSA 49 CFR §383) — primary market
  NON_CDL = 'NON_CDL',
  CDL_A = 'CDL_A',
  CDL_B = 'CDL_B',
  CDL_C = 'CDL_C',
  // EU / Turkey categories (secondary market)
  B = 'B',
  C = 'C',
  CE = 'CE',
  D = 'D',
  E = 'E',
}

export const DRIVER_LICENSE_TYPE_LABELS: Record<DriverLicenseType, string> = {
  [DriverLicenseType.NON_CDL]: 'Non-CDL (Class D)',
  [DriverLicenseType.CDL_A]: 'CDL Class A',
  [DriverLicenseType.CDL_B]: 'CDL Class B',
  [DriverLicenseType.CDL_C]: 'CDL Class C',
  [DriverLicenseType.B]: 'B (Car / Otomobil)',
  [DriverLicenseType.C]: 'C (Truck / Kamyon)',
  [DriverLicenseType.CE]: 'CE (Truck + Trailer / Kamyon + Römork)',
  [DriverLicenseType.D]: 'D (Bus / Otobüs)',
  [DriverLicenseType.E]: 'E (Trailer / Römork)',
};

/**
 * Single source of truth mapping AI-routing CDLClass codes ('A','B','C') to
 * canonical DriverLicenseType values. Consumed by the dispatch/AI layer when
 * matching trip CDL requirements to driver qualifications.
 */
export const CDL_TO_LICENSE_TYPE_MAP: Record<string, DriverLicenseType> = {
  A: DriverLicenseType.CDL_A,
  B: DriverLicenseType.CDL_B,
  C: DriverLicenseType.CDL_C,
  NON_CDL: DriverLicenseType.NON_CDL,
};

/**
 * Identity document types accepted for driver verification. USA types first
 * per USA-first MVP principle; TC_KIMLIK retained for the Turkey market.
 */
export enum IdentityDocumentType {
  SSN = 'SSN',
  DRIVERS_LICENSE = 'DRIVERS_LICENSE',
  PASSPORT = 'PASSPORT',
  TWIC = 'TWIC',
  TC_KIMLIK = 'TC_KIMLIK',
}

export const IDENTITY_DOCUMENT_TYPE_LABELS: Record<IdentityDocumentType, string> = {
  [IdentityDocumentType.SSN]: 'Social Security Number (SSN)',
  [IdentityDocumentType.DRIVERS_LICENSE]: "Driver's License",
  [IdentityDocumentType.PASSPORT]: 'Passport',
  [IdentityDocumentType.TWIC]: 'TWIC Card',
  [IdentityDocumentType.TC_KIMLIK]: 'TC Kimlik',
};
